import { useMemo, useState, type ReactNode } from 'react'
import {
  Badge,
  BadgeStatus,
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  FilterBar,
  Input,
  KolomForm,
  Modal,
  PageHeader,
  Select,
  Textarea,
  FILTER_KOSONG,
  type KolomTabel,
  type NilaiFilter,
} from '@/components'
import { api, buatIdempotencyKey, pesanKesalahan } from '@/lib/api'
import { formatRupiah, formatTanggal, formatTanggalJam } from '@/lib/format'
import type { BarisJurnal, Jurnal } from '@/lib/types'
import { useApi, usePaged, useAksi } from '@/lib/useApi'
import { useOpsiAkun, useOpsiUnit } from '@/lib/opsi'
import { useAuth } from '@/lib/auth'

const SUMBER = [
  'MANUAL',
  'OPENING_BALANCE',
  'INVOICE',
  'PAYMENT',
  'REFUND',
  'RECEIPT',
  'EXPENSE',
  'PURCHASE',
  'PAYROLL',
  'DISBURSEMENT',
  'INTERUNIT_TRANSFER',
  'BANK_ADJUSTMENT',
  'DEPRECIATION',
  'REVERSAL',
]

export default function JurnalPage() {
  const { punyaIzin } = useAuth()
  const opsiUnit = useOpsiUnit()
  const aksi = useAksi()
  const [filter, setFilter] = useState<NilaiFilter>({ ...FILTER_KOSONG, dari: `${new Date().getFullYear()}-01-01` })
  const [statusFilter, setStatusFilter] = useState('')
  const [sumberFilter, setSumberFilter] = useState('')
  const [detailId, setDetailId] = useState<string | null>(null)
  const [modalBuat, setModalBuat] = useState(false)
  const [konfirmasiBalik, setKonfirmasiBalik] = useState<Jurnal | null>(null)

  const daftar = usePaged<Jurnal>('/journals', {
    query: {
      dari: filter.dari || undefined,
      sampai: filter.sampai || undefined,
      unitId: filter.unitId || undefined,
      status: statusFilter || undefined,
      sourceType: sumberFilter || undefined,
    },
  })

  const kolom = useMemo<KolomTabel<Jurnal>[]>(
    () => [
      {
        kunci: 'journalNumber',
        judul: 'Nomor Jurnal',
        render: (b) => (
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="font-medium text-yayasan-800 hover:underline"
              onClick={() => setDetailId(b.id)}
            >
              {b.journalNumber}
            </button>
            {b.isReversal ? <Badge nada="peringatan">Pembalikan</Badge> : null}
          </div>
        ),
      },
      { kunci: 'journalDate', judul: 'Tanggal', render: (b) => formatTanggal(b.journalDate) },
      { kunci: 'description', judul: 'Uraian' },
      { kunci: 'unit', judul: 'Unit', render: (b) => b.unit?.name ?? '—' },
      { kunci: 'sourceType', judul: 'Sumber', render: (b) => b.sourceType },
      { kunci: 'totalDebit', judul: 'Debit', rata: 'kanan', render: (b) => formatRupiah(b.totalDebit) },
      { kunci: 'totalCredit', judul: 'Kredit', rata: 'kanan', render: (b) => formatRupiah(b.totalCredit) },
      { kunci: 'status', judul: 'Status', render: (b) => <BadgeStatus status={b.status} /> },
      {
        kunci: 'aksi',
        judul: 'Aksi',
        sortable: false,
        render: (b) => (
          <div className="flex flex-wrap gap-1.5">
            <Button ukuran="kecil" varian="garis" onClick={() => setDetailId(b.id)}>
              Baris
            </Button>
            {punyaIzin('journal:reverse') && b.status === 'POSTED' ? (
              <Button ukuran="kecil" varian="bahaya" onClick={() => setKonfirmasiBalik(b)}>
                Balik
              </Button>
            ) : null}
          </div>
        ),
      },
    ],
    [punyaIzin],
  )

  return (
    <div>
      <PageHeader
        judul="Jurnal"
        deskripsi="Jurnal umum terposting. Jurnal yang sudah diposting hanya dapat dikoreksi melalui pembalikan."
        ekspor={{
          path: '/reports/general-ledger',
          namaDasar: 'jurnal-umum',
          query: { dari: filter.dari || undefined, sampai: filter.sampai || undefined, unitId: filter.unitId || undefined },
        }}
        aksi={punyaIzin('journal:create') ? <Button onClick={() => setModalBuat(true)}>+ Jurnal Manual</Button> : null}
      />

      <FilterBar
        nilai={filter}
        onUbah={setFilter}
        unit={opsiUnit}
        tampilkan={{ unitId: true, dari: true, sampai: true, fundId: false, program: false, kategori: false }}
        ekstra={
          <div className="flex flex-wrap items-end gap-2">
            <Select
              label="Status"
              placeholder="Semua status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              opsi={['DRAFT', 'POSTED', 'REVERSED'].map((s) => ({ value: s, label: s }))}
              className="min-w-36"
            />
            <Select
              label="Sumber"
              placeholder="Semua sumber"
              value={sumberFilter}
              onChange={(e) => setSumberFilter(e.target.value)}
              opsi={SUMBER.map((s) => ({ value: s, label: s }))}
              className="min-w-44"
            />
          </div>
        }
      />

      <DataTable
        kolom={kolom}
        data={daftar.items}
        kunciBaris={(b) => b.id}
        memuat={daftar.memuat}
        galat={daftar.galat}
        kodeGalat={daftar.kodeGalat}
        onCoba={daftar.muat}
        meta={daftar.meta}
        onHalaman={daftar.setHalaman}
        onUrut={daftar.setUrutan}
        urutSaatIni={daftar.urutan}
        pesanKosong="Belum ada jurnal pada filter ini."
      />

      <DetailJurnal id={detailId} onTutup={() => setDetailId(null)} />
      <FormJurnalManual
        terbuka={modalBuat}
        onTutup={() => setModalBuat(false)}
        setelah={() => {
          setModalBuat(false)
          daftar.muat()
        }}
      />

      <ConfirmDialog
        terbuka={konfirmasiBalik !== null}
        onTutup={() => setKonfirmasiBalik(null)}
        judul="Pembalikan Jurnal"
        pesan={
          <>
            Jurnal <strong>{konfirmasiBalik?.journalNumber}</strong> ({formatRupiah(konfirmasiBalik?.totalDebit ?? 0)})
            akan dibalik dengan jurnal baru berlawanan. Tuliskan alasan pembalikan — wajib, minimal 10 karakter.
          </>
        }
        labelKonfirmasi="Balik Jurnal"
        labelAlasan="Alasan pembalikan jurnal"
        minimalAlasan={10}
        memuat={aksi.memuat}
        onKonfirmasi={async (alasan) => {
          if (!konfirmasiBalik || alasan.trim().length < 10) return
          const hasil = await aksi.jalankan(
            () =>
              api.post(
                `/journals/${konfirmasiBalik.id}/reverse`,
                { reason: alasan },
                { idempotencyKey: buatIdempotencyKey() },
              ),
            { sukses: 'Jurnal pembalik berhasil dibuat.' },
          )
          if (hasil !== null) {
            setKonfirmasiBalik(null)
            daftar.muat()
          }
        }}
      />
    </div>
  )
}

function DetailJurnal({ id, onTutup }: { id: string | null; onTutup: () => void }) {
  const { data, memuat, galat, kodeGalat, muat } = useApi<Jurnal>(id ? `/journals/${id}` : null)

  const kolom: KolomTabel<BarisJurnal>[] = [
    { kunci: 'lineNumber', judul: '#', lebar: '56px' },
    {
      kunci: 'account',
      judul: 'Akun',
      render: (b) => (
        <div>
          <p className="font-medium text-netral-800">{b.account?.name ?? b.accountId}</p>
          <p className="text-xs text-netral-500">{b.account?.code ?? ''}</p>
        </div>
      ),
    },
    { kunci: 'description', judul: 'Keterangan', render: (b) => b.description ?? '—' },
    {
      kunci: 'unit',
      judul: 'Unit',
      render: (b) => b.unit?.name ?? '—',
    },
    {
      kunci: 'debit',
      judul: 'Debit',
      rata: 'kanan',
      render: (b) => (b.debit ? formatRupiah(b.debit) : <span className="text-netral-300">—</span>),
    },
    {
      kunci: 'credit',
      judul: 'Kredit',
      rata: 'kanan',
      render: (b) => (b.credit ? formatRupiah(b.credit) : <span className="text-netral-300">—</span>),
    },
  ]

  const baris = data?.lines ?? []
  const totalDebit = baris.reduce((a, b) => a + (b.debit ?? 0), 0)
  const totalKredit = baris.reduce((a, b) => a + (b.credit ?? 0), 0)
  const seimbang = totalDebit === totalKredit

  return (
    <Modal
      terbuka={id !== null}
      onTutup={onTutup}
      judul={data ? `Jurnal ${data.journalNumber}` : 'Detail Jurnal'}
      lebar="max-w-5xl"
      footer={
        <>
          <Button varian="garis" onClick={muat}>
            Muat ulang
          </Button>
          <Button varian="halus" onClick={onTutup}>
            Tutup
          </Button>
        </>
      }
    >
      {galat ? (
        <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {galat} {kodeGalat ? `(${kodeGalat})` : ''}
        </p>
      ) : null}

      {data ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Info label="Tanggal">{formatTanggal(data.journalDate)}</Info>
            <Info label="Unit">{data.unit?.name ?? '—'}</Info>
            <Info label="Sumber">{data.sourceType}</Info>
            <Info label="Status">
              <BadgeStatus status={data.status} />
            </Info>
            <Info label="Uraian">{data.description}</Info>
            {data.sourceNumber ? <Info label="Dokumen Sumber">{data.sourceNumber}</Info> : null}
            {data.reversalReason ? <Info label="Alasan Pembalikan">{data.reversalReason}</Info> : null}
            {data.postedAt ? <Info label="Diposting">{formatTanggalJam(data.postedAt)}</Info> : null}
          </div>

          <div className="overflow-x-auto rounded-lg border border-netral-200">
            <table className="w-full text-sm">
              <thead className="bg-netral-50 text-left text-xs uppercase text-netral-600">
                <tr>
                  <th className="px-3 py-2">#</th>
                  <th className="px-3 py-2">Akun</th>
                  <th className="px-3 py-2">Keterangan</th>
                  <th className="px-3 py-2">Unit</th>
                  <th className="px-3 py-2 text-right">Debit</th>
                  <th className="px-3 py-2 text-right">Kredit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-netral-100">
                {memuat && baris.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-4 text-center text-netral-500">
                      Memuat baris jurnal…
                    </td>
                  </tr>
                ) : baris.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-4 text-center text-netral-500">
                      Tidak ada baris jurnal.
                    </td>
                  </tr>
                ) : (
                  baris.map((b) => (
                    <tr key={b.id}>
                      <td className="px-3 py-2">{b.lineNumber}</td>
                      <td className="px-3 py-2">{b.account ? `${b.account.code} — ${b.account.name}` : b.accountId}</td>
                      <td className="px-3 py-2">{b.description ?? '—'}</td>
                      <td className="px-3 py-2">{b.unit?.name ?? '—'}</td>
                      <td className="px-3 py-2 text-right">{b.debit ? formatRupiah(b.debit) : '—'}</td>
                      <td className="px-3 py-2 text-right">{b.credit ? formatRupiah(b.credit) : '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot className="bg-netral-50 font-semibold text-netral-800">
                <tr>
                  <td colSpan={4} className="px-3 py-2 text-right">
                    Total
                  </td>
                  <td className="px-3 py-2 text-right">{formatRupiah(totalDebit)}</td>
                  <td className="px-3 py-2 text-right">{formatRupiah(totalKredit)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="flex items-center gap-2">
            {seimbang ? (
              <Badge nada="sukses">Seimbang (debit = kredit)</Badge>
            ) : (
              <Badge nada="bahaya">
                Tidak seimbang — selisih {formatRupiah(Math.abs(totalDebit - totalKredit))}
              </Badge>
            )}
          </div>
        </div>
      ) : memuat ? (
        <p className="text-sm text-netral-500">Memuat jurnal…</p>
      ) : null}
    </Modal>
  )
}

interface BarisFormJurnal {
  accountId: string
  debit: string
  credit: string
  description: string
}

function FormJurnalManual({
  terbuka,
  onTutup,
  setelah,
}: {
  terbuka: boolean
  onTutup: () => void
  setelah: () => void
}) {
  const opsiUnit = useOpsiUnit()
  const akun = useOpsiAkun({ hanyaPostable: true })
  const [unitId, setUnitId] = useState('')
  const [journalDate, setJournalDate] = useState(new Date().toISOString().slice(0, 10))
  const [description, setDescription] = useState('')
  const [baris, setBaris] = useState<BarisFormJurnal[]>([
    { accountId: '', debit: '', credit: '', description: '' },
    { accountId: '', debit: '', credit: '', description: '' },
  ])
  const [galat, setGalat] = useState<string | null>(null)
  const [memuat, setMemuat] = useState(false)

  const totalDebit = baris.reduce((a, b) => a + (Number(b.debit.replace(/[^0-9.-]/g, '')) || 0), 0)
  const totalKredit = baris.reduce((a, b) => a + (Number(b.credit.replace(/[^0-9.-]/g, '')) || 0), 0)
  const seimbang = totalDebit === totalKredit && totalDebit > 0

  const simpan = async () => {
    setGalat(null)
    if (!unitId) return setGalat('Unit wajib dipilih.')
    if (description.trim().length < 3) return setGalat('Uraian jurnal minimal 3 karakter.')
    const barisValid = baris.filter((b) => b.accountId && (Number(b.debit) > 0 || Number(b.credit) > 0))
    if (barisValid.length < 2) return setGalat('Jurnal minimal memiliki dua baris.')
    if (!seimbang) return setGalat('Total debit harus sama dengan total kredit.')

    setMemuat(true)
    try {
      await api.post(
        '/journals',
        {
          unitId,
          journalDate,
          description: description.trim(),
          sourceType: 'MANUAL',
          lines: barisValid.map((b) => ({
            accountId: b.accountId,
            debit: Number(b.debit.replace(/[^0-9.-]/g, '')) || 0,
            credit: Number(b.credit.replace(/[^0-9.-]/g, '')) || 0,
            description: b.description.trim() || undefined,
          })),
        },
        { idempotencyKey: buatIdempotencyKey() },
      )
      setelah()
    } catch (e) {
      setGalat(pesanKesalahan(e))
    } finally {
      setMemuat(false)
    }
  }

  return (
    <Modal
      terbuka={terbuka}
      onTutup={onTutup}
      judul="Jurnal Manual"
      deskripsi="Akun induk tidak dapat diposting. Total debit harus sama dengan total kredit."
      lebar="max-w-4xl"
      footer={
        <>
          <Button varian="garis" onClick={onTutup} disabled={memuat}>
            Batal
          </Button>
          <Button onClick={() => void simpan()} memuat={memuat} disabled={!seimbang}>
            Posting Jurnal
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {galat ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {galat}
          </div>
        ) : null}

        <KolomForm kolom={2}>
          <Select
            label="Unit"
            placeholder="Pilih unit"
            opsi={opsiUnit}
            value={unitId}
            onChange={(e) => setUnitId(e.target.value)}
            required
          />
          <Input
            type="date"
            label="Tanggal Jurnal"
            value={journalDate}
            onChange={(e) => setJournalDate(e.target.value)}
            required
          />
        </KolomForm>

        <Textarea
          label="Uraian Jurnal"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          required
        />

        <Card padat className="p-3">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-netral-800">Baris Debit / Kredit</h3>
            <Button
              ukuran="kecil"
              varian="garis"
              onClick={() => setBaris((b) => [...b, { accountId: '', debit: '', credit: '', description: '' }])}
            >
              + Tambah Baris
            </Button>
          </div>

          <div className="space-y-2">
            {baris.map((b, i) => (
              <div key={i} className="grid grid-cols-1 gap-2 lg:grid-cols-[1fr_140px_140px_1fr_auto]">
                <Select
                  placeholder={akun.memuat ? 'Memuat akun…' : 'Pilih akun'}
                  opsi={akun.opsi}
                  value={b.accountId}
                  onChange={(e) =>
                    setBaris((arr) => arr.map((x, j) => (j === i ? { ...x, accountId: e.target.value } : x)))
                  }
                />
                <Input
                  placeholder="Debit"
                  inputMode="numeric"
                  value={b.debit}
                  onChange={(e) =>
                    setBaris((arr) =>
                      arr.map((x, j) => (j === i ? { ...x, debit: e.target.value, credit: '' } : x)),
                    )
                  }
                />
                <Input
                  placeholder="Kredit"
                  inputMode="numeric"
                  value={b.credit}
                  onChange={(e) =>
                    setBaris((arr) =>
                      arr.map((x, j) => (j === i ? { ...x, credit: e.target.value, debit: '' } : x)),
                    )
                  }
                />
                <Input
                  placeholder="Keterangan baris"
                  value={b.description}
                  onChange={(e) =>
                    setBaris((arr) => arr.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)))
                  }
                />
                <Button
                  varian="halus"
                  ukuran="kecil"
                  onClick={() => setBaris((arr) => (arr.length > 2 ? arr.filter((_, j) => j !== i) : arr))}
                >
                  Hapus
                </Button>
              </div>
            ))}
          </div>

          <div className="mt-3 flex flex-wrap items-center justify-end gap-4 border-t border-netral-100 pt-3 text-sm">
            <span>
              Total Debit: <strong className="angka-tabular">{formatRupiah(totalDebit)}</strong>
            </span>
            <span>
              Total Kredit: <strong className="angka-tabular">{formatRupiah(totalKredit)}</strong>
            </span>
            {seimbang ? <Badge nada="sukses">Seimbang</Badge> : <Badge nada="bahaya">Belum seimbang</Badge>}
          </div>
        </Card>
      </div>
    </Modal>
  )
}

function Info({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium tracking-wide text-netral-500 uppercase">{label}</p>
      <p className="mt-0.5 text-sm break-words text-netral-800">{children}</p>
    </div>
  )
}
